import type * as React from "react";
import { useMemo, useState } from "react";

import { gql, useMutation } from "@apollo/client";

const CREATE_FAQ_PUBLIC = gql`
  mutation CreateFaqPublic($input: CreateFaqPublicInput!) {
    createSignatureProfileFaqPublic(input: $input) {
      faq {
        id
        question
        moderatedByWebsiteOwner
      }
      errors
    }
  }
`;

export const SignatureProfileFaq = ({ profile }: { profile: any }) => {
  const firstName: string | undefined = profile?.artistFirstName || undefined;
  const faqs: any[] = useMemo(
    () => (Array.isArray(profile?.faqs) ? profile.faqs : []),
    [profile]
  );
  const hasFaqs = faqs.length > 0;
  const [openFaqId, setOpenFaqId] = useState<string | number | null>(null);
  const [visitorName, setVisitorName] = useState("");
  const [visitorEmail, setVisitorEmail] = useState("");
  const [questionText, setQuestionText] = useState("");
  const [submissionMessage, setSubmissionMessage] = useState<string | null>(
    null
  );
  const [createFaqPublic, { loading: submitting }] =
    useMutation(CREATE_FAQ_PUBLIC);

  const handleToggle = (id: string | number) => {
    setOpenFaqId((current) => (current === id ? null : id));
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();
    setSubmissionMessage(null);
    if (!profile?.id) return;
    try {
      const { data } = await createFaqPublic({
        variables: {
          input: {
            signatureProfileId: String(profile.id),
            question: questionText,
          },
        },
      });

      const errors: string[] | undefined =
        data?.createSignatureProfileFaqPublic?.errors;

      if (errors && errors.length > 0) {
        setSubmissionMessage(errors.join(", "));
      } else {
        setSubmissionMessage("Your question has been submitted.");
        setQuestionText("");
      }
    } catch (error) {
      setSubmissionMessage("Failed to submit. Please try again later.");
    }
  };

  return (
    <section className="py-16 bg-artist-offwhite">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="font-playfair text-3xl font-bold mb-3">
              Frequently Asked Questions
            </h2>
            {/* 24. Signature profile first name */}
            {firstName ? (
              <p className="text-muted-foreground">
                {`Everything you need to know about working with ${firstName}`}
              </p>
            ) : null}
          </div>

          {/* 25. Signature profile frequently asked questions */}
          {hasFaqs ? (
            <div
              className="bg-white rounded-lg shadow-sm"
              data-orientation="vertical"
            >
              {faqs.map((faq) => {
                const isOpen = openFaqId === faq.id;
                const contentStyle = {
                  "--radix-accordion-content-height":
                    "var(--radix-collapsible-content-height)" as unknown as string,
                  "--radix-accordion-content-width":
                    "var(--radix-collapsible-content-width)" as unknown as string,
                } as React.CSSProperties;

                return (
                  <div
                    key={faq.id}
                    data-state={isOpen ? "open" : "closed"}
                    data-orientation="vertical"
                    className="border-b"
                  >
                    <h3
                      data-orientation="vertical"
                      data-state={isOpen ? "open" : "closed"}
                      className="flex"
                    >
                      <button
                        type="button"
                        aria-expanded={isOpen}
                        data-state={isOpen ? "open" : "closed"}
                        data-orientation="vertical"
                        className="flex flex-1 items-center justify-between transition-all [&[data-state=open]>svg]:rotate-180 px-6 py-4 text-left font-medium hover:no-underline"
                        onClick={() => handleToggle(faq.id)}
                      >
                        {faq.question}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="lucide lucide-chevron-down h-4 w-4 shrink-0 transition-transform duration-200"
                        >
                          <path d="m6 9 6 6 6-6" />
                        </svg>
                      </button>
                    </h3>
                    <div
                      data-state={isOpen ? "open" : "closed"}
                      role="region"
                      data-orientation="vertical"
                      className="overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down px-6 pb-4"
                      style={contentStyle}
                    >
                      {isOpen ? (
                        <div className="text-muted-foreground">
                          {faq.answer}
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}

          {/* 26. Signature profile ask a question form */}
          {profile?.id ? (
            <div className="mt-10 bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-playfair text-xl font-medium mb-4 flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-circle-help mr-2 text-artist-accent"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                  <path d="M12 17h.01" />
                </svg>
                Ask a Question
              </h3>
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Your Name"
                    value={visitorName}
                    onChange={(e) => setVisitorName(e.target.value)}
                    className="w-full px-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-artist-accent focus:border-transparent"
                  />
                </div>
                <div className="mb-4">
                  <input
                    type="email"
                    placeholder="Your Email"
                    value={visitorEmail}
                    onChange={(e) => setVisitorEmail(e.target.value)}
                    className="w-full px-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-artist-accent focus:border-transparent"
                  />
                </div>
                <div className="mb-4">
                  <textarea
                    placeholder="Your Question"
                    rows={4}
                    value={questionText}
                    onChange={(e) => setQuestionText(e.target.value)}
                    className="w-full px-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-artist-accent focus:border-transparent"
                    required
                  />
                </div>
                {submissionMessage ? (
                  <div className="mb-4 text-sm text-muted-foreground">
                    {submissionMessage}
                  </div>
                ) : null}
                <button
                  type="submit"
                  disabled={submitting || !questionText.trim()}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2 w-full rounded-full"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-send-horizontal mr-2"
                  >
                    <path d="M3.714 3.048a.498.498 0 0 0-.683.627l2.843 7.627a2 2 0 0 1 0 1.396l-2.842 7.627a.498.498 0 0 0 .682.627l18-8.5a.5.5 0 0 0 0-.904z" />
                    <path d="M6 12h16" />
                  </svg>
                  {submitting ? "Submitting..." : "Submit Question"}
                </button>
              </form>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default SignatureProfileFaq;
